/*
Purpose: View switcher, date navigation, density and create actions for Calendar (v2).
*/

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';

import { CALENDAR_VIEW, DENSITY, HOURS_FILTER } from '../constants';

/** A grouped set of mutually exclusive buttons. */
function Segment({ options, value, onChange, ariaLabel }) {
  return (
    <div className="cv2-segment" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`cv2-segment__btn${option.value === value ? ' is-active' : ''}`}
          onClick={() => onChange(option.value)}
          aria-pressed={option.value === value}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

Segment.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
};

const VIEW_OPTIONS = [
  { value: CALENDAR_VIEW.DAY, label: 'Day' },
  { value: CALENDAR_VIEW.WEEK, label: 'Week' },
];

const HOURS_OPTIONS = [
  { value: HOURS_FILTER.OPERATING_HOURS, label: 'Operating hours' },
  { value: HOURS_FILTER.ALL_HOURS, label: 'All hours' },
];

const DENSITY_OPTIONS = [
  { value: DENSITY.DETAILED, label: 'Detailed' },
  { value: DENSITY.COMPACT, label: 'Compact' },
];

export default function CalendarToolbar({
  view,
  onViewChange,
  hoursFilter,
  onHoursFilterChange,
  density,
  onDensityChange,
  dateLabel,
  selectedDate,
  onDateChange,
  onPrevious,
  onNext,
  onToday,
  onRefresh,
  isRefreshing,
  onAddEnrolment,
  onCreateSession,
}) {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateNavRef = useRef(null);

  useEffect(() => {
    if (!showDatePicker) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (dateNavRef.current && !dateNavRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setShowDatePicker(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showDatePicker]);

  function handleDateSelect(value) {
    if (!value) {
      return;
    }
    onDateChange(value.format('YYYY-MM-DD'));
    setShowDatePicker(false);
  }

  return (
    <div className="cv2-toolbar">
      <Segment
        options={VIEW_OPTIONS}
        value={view}
        onChange={onViewChange}
        ariaLabel="Calendar view"
      />

      <div className="cv2-datenav" ref={dateNavRef}>
        <button
          type="button"
          className="cv2-datenav__btn"
          onClick={onPrevious}
          aria-label="Previous period"
        >
          <ChevronLeftIcon className="cv2-icon" />
        </button>
        <button type="button" className="cv2-datenav__btn" onClick={onToday}>
          Today
        </button>
        <button
          type="button"
          className={`cv2-datenav__label cv2-datenav__label--picker${
            showDatePicker ? ' is-open' : ''
          }`}
          onClick={() => setShowDatePicker((open) => !open)}
          aria-haspopup="dialog"
          aria-expanded={showDatePicker}
          title="Pick a date"
        >
          {dateLabel}
        </button>
        <button
          type="button"
          className="cv2-datenav__btn"
          onClick={onNext}
          aria-label="Next period"
        >
          <ChevronRightIcon className="cv2-icon" />
        </button>

        {showDatePicker ? (
          <div className="cv2-datenav__popover" role="dialog" aria-label="Choose date">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={dayjs(selectedDate, 'YYYY-MM-DD')}
                onChange={handleDateSelect}
              />
            </LocalizationProvider>
          </div>
        ) : null}
      </div>

      <Segment
        options={HOURS_OPTIONS}
        value={hoursFilter}
        onChange={onHoursFilterChange}
        ariaLabel="Visible hours"
      />

      <Segment
        options={DENSITY_OPTIONS}
        value={density}
        onChange={onDensityChange}
        ariaLabel="Card density"
      />

      <div className="cv2-toolbar__right">
        <button type="button" className="cv2-btn cv2-btn--enrol" onClick={onAddEnrolment}>
          <AddIcon className="cv2-icon" />
          Add Enrolment
        </button>
        <button type="button" className="cv2-segment__btn is-active" onClick={onCreateSession}>
          <AddIcon className="cv2-icon" />
          Create Session
        </button>
        <button
          type="button"
          className="cv2-btn cv2-btn--icon"
          onClick={onRefresh}
          disabled={isRefreshing}
          title="Refresh calendar"
        >
          <RefreshIcon className="cv2-icon" />
        </button>
      </div>
    </div>
  );
}

CalendarToolbar.propTypes = {
  view: PropTypes.string.isRequired,
  onViewChange: PropTypes.func.isRequired,
  hoursFilter: PropTypes.string.isRequired,
  onHoursFilterChange: PropTypes.func.isRequired,
  density: PropTypes.string.isRequired,
  onDensityChange: PropTypes.func.isRequired,
  dateLabel: PropTypes.string.isRequired,
  selectedDate: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onToday: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool,
  onAddEnrolment: PropTypes.func.isRequired,
  onCreateSession: PropTypes.func.isRequired,
};

CalendarToolbar.defaultProps = {
  isRefreshing: false,
};
